import { getToken, onMessage } from "firebase/messaging";
import { useEffect, useRef } from "react";

import { getFirebaseMessaging } from "@/firebase/firebase.config";
import { useAppConfiguration } from "@/lib/app/configuration/configuration.app.hook";
import {
  getPushPreferences,
  registerPushToken,
  unregisterPushToken,
} from "@/services/API/notifications/notifications.api.service";

export const isInReactNativeWebView = !!(window as typeof window & { ReactNativeWebView?: unknown })
  .ReactNativeWebView;
const PUSH_PERMISSION_REQUESTED_KEY = "pushPermissionRequested";

interface RnPushNotificationDetail {
  notification?: { title?: string; body?: string };
  data?: Record<string, string>;
}

export type ForegroundPushNotification = {
  title: string;
  body: string;
  open: () => void;
};

function openNotificationUrl(url?: string): void {
  const targetUrl = new URL(url ?? "/alarms", window.location.origin);
  if (targetUrl.origin !== window.location.origin) return;

  window.focus();
  window.location.assign(targetUrl.href);
}

function getOrCreateDeviceId(): string {
  const storedDeviceId = localStorage.getItem("fcmDeviceId");
  if (storedDeviceId) return storedDeviceId;

  const randomId =
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

  localStorage.setItem("fcmDeviceId", randomId);
  return randomId;
}

export function usePushNotifications(
  enabled: boolean,
  onForegroundNotification: (notification: ForegroundPushNotification) => void
): void {
  const deviceIdRef = useRef<string>(getOrCreateDeviceId());
  const { appConfiguration } = useAppConfiguration();
  const vapidKey = appConfiguration?.firebaseVapidKey ?? null;

  useEffect(() => {
    console.log("[Push] Hook state", {
      enabled,
      hasVapidKey: Boolean(vapidKey),
      isInWebView: isInReactNativeWebView,
    });

    if (!enabled) return;

    let unsubscribeMessage: (() => void) | undefined;
    let rnListener: ((event: Event) => void) | undefined;

    if (isInReactNativeWebView) {
      // Inside React Native WebView — do NOT register FCM, just listen for bridge events
      rnListener = (event: Event) => {
        const detail = (event as CustomEvent<RnPushNotificationDetail>).detail;
        if (detail?.notification?.title) {
          // Native React Native displays the notification. The WebView may not expose
          // the browser Notification API, so do not create a second notification here.
          console.log("[Push] Native notification received", {
            title: detail.notification.title,
            data: detail.data,
          });
        }
      };
      window.addEventListener("rnPushNotification", rnListener);
      return () => {
        if (rnListener) window.removeEventListener("rnPushNotification", rnListener);
      };
    }

    // Running in a regular browser — register FCM
    const initFcm = async (requestPermission = false) => {
      if (!("Notification" in window) || !("serviceWorker" in navigator)) {
        console.log("[Push] Browser does not support notifications or service workers");
        return;
      }
      if (!vapidKey) {
        console.log("[Push] Firebase VAPID key is not available");
        return;
      }

      const { pushNotificationsEnabled } = await getPushPreferences().catch(() => ({
        pushNotificationsEnabled: true,
      }));
      if (!pushNotificationsEnabled) return;

      const storedToken = localStorage.getItem("fcmToken");
      const shouldRequestPermission =
        requestPermission || localStorage.getItem(PUSH_PERMISSION_REQUESTED_KEY) !== "true";
      const permission = shouldRequestPermission
        ? await Notification.requestPermission()
        : Notification.permission;
      if (shouldRequestPermission) {
        localStorage.setItem(PUSH_PERMISSION_REQUESTED_KEY, "true");
      }
      console.log("[Push] Notification permission", permission);
      localStorage.setItem("fcmDeviceId", deviceIdRef.current);

      if (storedToken && permission !== "granted") {
        console.log("[Push] Re-registering stored token", {
          deviceId: deviceIdRef.current,
          tokenSuffix: storedToken.slice(-12),
          permission,
        });
        await registerPushToken(storedToken, "web", deviceIdRef.current, permission).catch(
          (error: unknown) => {
            console.error("[Push] Failed to synchronize stored token", error);
          }
        );
      }

      if (permission !== "granted") return;

      const swRegistration = await navigator.serviceWorker
        .register("/firebase-messaging-sw.js")
        .catch(() => null);

      if (!swRegistration) {
        console.log("[Push] Service worker registration failed");
        return;
      }
      console.log("[Push] Service worker registered", swRegistration.scope);

      const messaging = getFirebaseMessaging();
      if (!messaging) return;

      const token = await getToken(messaging, {
        vapidKey,
        serviceWorkerRegistration: swRegistration,
      }).catch(() => null);

      if (!token) {
        console.log("[Push] Firebase token was not returned");
        return;
      }

      localStorage.setItem("fcmToken", token);

      console.log("[Push] Token obtained", {
        deviceId: deviceIdRef.current,
        tokenSuffix: token.slice(-12),
        changed: token !== storedToken,
      });

      // Registration is idempotent by deviceId. Always retry it, including
      // when the FCM token has not changed, because the backend may not have
      // persisted a previous registration attempt.
      console.log("[Push] Registering token in backend", {
        deviceId: deviceIdRef.current,
        tokenSuffix: token.slice(-12),
        permission,
      });
      await registerPushToken(token, "web", deviceIdRef.current, permission).catch(
        (error: unknown) => {
          console.error("[Push] Failed to register token", error);
        }
      );

      // Foreground messages
      unsubscribeMessage = onMessage(messaging, (payload) => {
        const title = payload.notification?.title ?? payload.data?.title;
        const body = payload.notification?.body ?? payload.data?.body;
        if (title && Notification.permission === "granted") {
          console.log("[Push] Foreground message received", {
            title,
            url: payload.data?.url,
          });
          onForegroundNotification({
            title,
            body: body ?? "",
            open: () => openNotificationUrl(payload.data?.url),
          });
        }
      });
    };

    const serviceWorkerMessageListener = (event: MessageEvent) => {
      if (event.data?.type === "PUSH_NOTIFICATION_CLICK") {
        console.log("[Push] Service worker processed notification click", {
          url: event.data.url,
        });
      }
    };
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.addEventListener("message", serviceWorkerMessageListener);
    }

    const preferenceChangedListener = () => {
      initFcm(true).catch((error: unknown) => {
        console.error("[Push] Failed to apply changed preference", error);
      });
    };
    window.addEventListener("pushPreferenceChanged", preferenceChangedListener);
    initFcm(true);

    return () => {
      unsubscribeMessage?.();
      const token = localStorage.getItem("fcmToken");
      if (token && enabled) {
        console.log("[Push] Unregistering token on logout", {
          deviceId: deviceIdRef.current,
          tokenSuffix: token.slice(-12),
        });
        unregisterPushToken(token).catch((error: unknown) => {
          console.error("[Push] Failed to unregister token", error);
        });
      }
      if ("serviceWorker" in navigator) {
        navigator.serviceWorker.removeEventListener("message", serviceWorkerMessageListener);
      }
      window.removeEventListener("pushPreferenceChanged", preferenceChangedListener);
    };
  }, [enabled, onForegroundNotification, vapidKey]);
}
